import { StoryStep } from './StoryStep';
import { fmtMoney, fmtPct } from './story-utils';
import type { AiInsight } from '@/types/sem';

interface ImpactStepProps {
  pageCvr: number;
  portfolioAvgCvr: number;
  qualityScore: number;
  wastedSpend: number;
  clicksLostPct: number;
  insight?: AiInsight;
}

export function ImpactStep({ pageCvr, portfolioAvgCvr, qualityScore, wastedSpend, clicksLostPct, insight }: ImpactStepProps) {
  // Estimate CPC inflation from low quality score
  // QS 10 = baseline, each point below ~inflates CPC by ~16%
  const cpcInflation = qualityScore < 10 ? Math.round((10 - qualityScore) * 16 / (qualityScore || 1)) : 0;

  return (
    <StoryStep
      label="The Impact"
      collapsedStats={
        <>
          <div className="story-acc-stat">
            <span className="label">CVR</span>
            <span>{fmtPct(pageCvr)}</span>
          </div>
          <div className="story-acc-stat">
            <span className="label">Wasted</span>
            <span className="story-val-red">{fmtMoney(wastedSpend)}</span>
          </div>
        </>
      }
    >
      <div className="story-grid3">
        <div className="story-gc">
          <div className="story-gc-lbl">Conv Rate</div>
          <div className="story-gc-val">{fmtPct(pageCvr)}</div>
          <div className="story-gc-sub">portfolio avg: {fmtPct(portfolioAvgCvr)}</div>
        </div>
        <div className="story-gc">
          <div className="story-gc-lbl">Quality Score</div>
          <div className="story-gc-val">
            {Math.round(qualityScore)}<sup>/10</sup>
          </div>
          <div className="story-gc-sub">
            {cpcInflation > 0 ? `inflating CPC ~${cpcInflation}%` : 'CPC at baseline'}
          </div>
        </div>
        <div className="story-gc">
          <div className="story-gc-lbl">Wasted Spend</div>
          <div className="story-gc-val story-gc-val-red">{fmtMoney(wastedSpend)}</div>
          <div className="story-gc-sub">{clicksLostPct}% of clicks lost</div>
        </div>
      </div>
      {insight?.optimization_notes && (
        <div className="mt-4 pt-4 border-t border-[var(--ed-rule)]">
          <p className="text-[10px] text-[var(--ed-t4)] uppercase tracking-wider mb-2">
            AI Analysis
          </p>
          <p className="text-sm text-[var(--ed-t2)] leading-relaxed">
            {insight.optimization_notes}
          </p>
          {insight.estimated_impact && (
            <span className="inline-block mt-2 text-xs font-medium px-2 py-1 rounded-full bg-[var(--ed-green-bg)] text-[var(--ed-green)] border border-[rgba(74,186,122,0.12)]">
              Projected: {insight.estimated_impact}
            </span>
          )}
        </div>
      )}
    </StoryStep>
  );
}
